import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Headset } from "lucide-react";
import { toast } from "sonner";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { CONTACT } from "@/mock";
import { apiCreateEnquiry } from "@/api";

const INFO = [
  { icon: Phone, label: "Call us 24x7", value: CONTACT.phonePretty, href: `tel:${CONTACT.phone}` },
  { icon: Mail, label: "Email us", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Clock, label: "Support hours", value: "Round the clock · 365 days", href: null },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill your name, phone and message");
      return;
    }
    setSending(true);
    apiCreateEnquiry({
      type: "contact",
      name: form.name,
      phone: form.phone,
      email: form.email,
      details: { subject: form.subject, message: form.message },
    })
      .then(() => {
        toast.success("Message sent! Our team will get back to you shortly.");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      })
      .catch(() => toast.error("Could not send. Please try again or call us."))
      .finally(() => setSending(false));
  };

  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.address)}&z=15&output=embed`;

  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid="contact-page">
      <TopBar />
      <Header />

      <section className="relative overflow-hidden bg-gradient-to-b from-[#0A0F2E] to-[#1B1F5B] py-14">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F5B32B]"><Headset className="h-7 w-7 text-[#0A0F2E]" /></span>
          <h1 className="mt-4 font-[Poppins] text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">Contact Us</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/70 md:text-base">
            Questions about a booking, a refund or a great fare? Our travel experts are here 24x7 — reach out and we'll respond fast.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-6 lg:grid-cols-[1fr_1.15fr]">
          {/* Left — info + map */}
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {INFO.map((it, i) => {
                const Icon = it.icon;
                const inner = (
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-colors hover:border-[#1B1F5B]/30">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1B1F5B]/[0.06]"><Icon className="h-5 w-5 text-[#1B1F5B]" /></span>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{it.label}</p>
                      <p className="text-sm font-bold text-slate-800">{it.value}</p>
                    </div>
                  </div>
                );
                return it.href ? (
                  <a key={i} href={it.href} data-testid={`contact-info-${i}`}>{inner}</a>
                ) : (
                  <div key={i} data-testid={`contact-info-${i}`}>{inner}</div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="flex items-start gap-2 text-sm text-slate-600">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#F5B32B]" />
                <span><span className="font-bold text-slate-800">{CONTACT.company}</span><br />{CONTACT.address}</span>
              </p>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm" data-testid="contact-map">
              <iframe
                title="Office location"
                src={mapSrc}
                className="h-[300px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right — form */}
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_15px_50px_rgba(27,31,91,0.10)] md:p-8">
            <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-800"><MessageCircle className="h-5 w-5 text-[#1B1F5B]" /> Send us a message</h2>
            <p className="mt-1 text-sm text-slate-500">Fill the form and we'll get back within a few hours.</p>
            <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="Mobile number *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
              <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B] sm:col-span-2" />
              <input data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Subject (e.g. Refund status, PNR ABC123)" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B] sm:col-span-2" />
              <textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help you? *" rows={5} className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B] sm:col-span-2" />
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 rounded-xl bg-[#1B1F5B] py-3.5 text-sm font-extrabold tracking-wider text-white transition-colors hover:bg-[#141844] disabled:opacity-60 sm:col-span-2"
              >
                <Send className="h-4 w-4" /> {sending ? "SENDING..." : "SEND MESSAGE"}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ContactPage;
